import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, RefreshCw, CheckCircle2, XCircle, ShieldAlert, Loader2 } from "lucide-react";
import api from "@/lib/api";

export function statusMeta(s) {
  const m = {
    pending: { label: "Pending", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
    pending_verification: { label: "Verifying on BSC", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
    queued: { label: "Pending", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
    verifying: { label: "Verifying on BSC", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
    approved: { label: "Confirmed", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
    confirmed: { label: "Confirmed", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
    already_processed: { label: "Confirmed", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
    rejected: { label: "Rejected", cls: "text-rose-400 bg-rose-500/10 border-rose-500/30" },
    failed: { label: "Failed", cls: "text-rose-400 bg-rose-500/10 border-rose-500/30" },
    manual_review: { label: "Manual Review", cls: "text-orange-300 bg-orange-500/10 border-orange-500/30" },
  };
  return m[s] || { label: s ? s.replace(/_/g, " ") : "Pending", cls: "text-white/70 bg-white/5 border-white/10" };
}

export function shortHash(h) { return h ? `${h.slice(0, 6)}…${h.slice(-4)}` : ""; }

export function BscScanLink({ hash }) {
  if (!hash) return <span className="text-[var(--text-dim)]">—</span>;
  return (
    <a href={`https://bscscan.com/tx/${hash}`} target="_blank" rel="noopener noreferrer"
       className="inline-flex items-center gap-1 text-[var(--gold)] hover:underline" data-testid="bscscan-link" title={hash}>
      {shortHash(hash)} <ExternalLink size={12} />
    </a>
  );
}

const TERMINAL = ["approved", "confirmed", "already_processed", "rejected", "failed", "manual_review"];
const GREEN = ["approved", "confirmed", "already_processed"];
const FRIENDLY_REASON = {
  no_matching_transfer_to_deposit_address: "No matching USDT transfer to the deposit address",
  invalid_tx_hash_format: "Invalid transaction hash format",
  tx_failed_onchain: "Transaction failed on-chain",
  multiple_transfers_to_recipient: "Multiple transfers detected — manual review",
};

export default function DepositStatusCard({ depositId, onDone }) {
  const [info, setInfo] = useState(null);
  const doneRef = useRef(false);
  useEffect(() => {
    doneRef.current = false;
    let stop = false, tries = 0, timer;
    const poll = async () => {
      if (stop) return;
      tries += 1;
      try {
        const { data } = await api.get(`/user/deposits/${depositId}`);
        setInfo(data);
        const st = data.verification?.status || data.status;
        if (TERMINAL.includes(st)) {
          if (!doneRef.current && GREEN.includes(st)) { doneRef.current = true; onDone && onDone(); }
          return; // stop polling on terminal state
        }
      } catch (e) { /* keep retrying quietly */ }
      timer = setTimeout(poll, Math.min(4000 + tries * 500, 12000));
    };
    poll();
    return () => { stop = true; clearTimeout(timer); };
  }, [depositId]); // eslint-disable-line

  if (!info) return null;
  const st = info.verification?.status || info.status;
  const meta = statusMeta(st);
  const confs = info.verification?.confirmations;
  const req = info.required_confirmations;
  const green = GREEN.includes(st);
  const bad = ["rejected", "failed"].includes(st);
  const spinning = ["pending", "verifying", "queued", "pending_verification"].includes(st);
  const Icon = green ? CheckCircle2 : (bad ? XCircle : (st === "manual_review" ? ShieldAlert : Loader2));
  return (
    <div className={`rounded-2xl border p-5 ${meta.cls}`} data-testid="deposit-status-card">
      <div className="flex items-center gap-2">
        <Icon size={20} className={spinning ? "animate-spin" : ""} />
        <span className="text-lg font-bold" data-testid="deposit-status-label">{meta.label}</span>
        <span className="ml-auto text-xs opacity-80">Deposit {info.deposit_id}</span>
      </div>
      {confs != null && !green && !bad && (
        <p className="mt-2 text-sm" data-testid="deposit-confirmations">Confirmations: <b>{confs}{req ? ` / ${req}` : ""}</b></p>
      )}
      {green && info.amount != null && <p className="mt-2 text-sm text-white">Credited: <b>${info.amount}</b> USDT</p>}
      {["rejected", "failed", "manual_review"].includes(st) && info.verification?.reason && (
        <p className="mt-2 text-sm">{FRIENDLY_REASON[info.verification.reason] || "Requires manual verification"}</p>
      )}
      {info.tx_hash && <p className="mt-2 text-xs">Tx: <BscScanLink hash={info.tx_hash} /></p>}
      {!TERMINAL.includes(st) && <p className="mt-2 flex items-center gap-1 text-xs opacity-70"><RefreshCw size={11} className="animate-spin" /> Auto-refreshing…</p>}
    </div>
  );
}
